import type { ReactNode } from "react";
import { Link } from "react-router";
import { AlertTriangle, Info, Trash2 } from "lucide-react";
import type { CartLine } from "../../api/cart";
import { formatPesos } from "../../lib/money";
import { ReferenceNumber, unitLabel } from "../catalogue/partLabels";
import { Button, Icon, QuantityStepper } from "../ui";
import { cx } from "../ui/cx";
import { quantityUnit } from "./units";

export interface CartLineRowProps {
  line: CartLine;
  /** Whether this line is ticked for checkout (selective checkout). */
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onQuantity: (quantity: number) => void;
  onRemove: () => void;
  busy: boolean;
  error?: string;
}

/**
 * One basket line: the part, how many, and today's price. A line that can't be ordered as it
 * stands says why and what to do; nothing is dropped behind the customer's back (PRD 5.5).
 * The checkbox at the left ticks it for checkout; unticked lines stay in the basket.
 */
export function CartLineRow({ line, selected, onSelect, onQuantity, onRemove, busy, error }: CartLineRowProps) {
  const listing = line.listing;
  const remove = (
    <Button variant="quiet" size="sm" icon={Trash2} disabled={busy} onClick={onRemove} aria-label={listing ? `Remove ${listing.name}` : "Remove this part"}>
      <span className="md:sr-only">Remove</span>
    </Button>
  );
  // The visual box stays 20px, but on a phone the tappable label is padded out to 44px and
  // the negative margin absorbs the padding so the row's spacing is unchanged (KALI-46).
  const tick = (
    <label className="-m-3 flex flex-none cursor-pointer items-center justify-center p-3 md:m-0 md:p-0">
      <input
        type="checkbox"
        checked={selected}
        onChange={(event) => onSelect(event.target.checked)}
        disabled={busy}
        aria-label={listing ? `Check out ${listing.name}` : "Check out this part"}
        className="size-5 cursor-pointer accent-green-700 focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-40"
      />
    </label>
  );

  if (!listing) {
    return (
      <li className="flex flex-wrap items-center gap-3 rounded-md border border-border-subtle bg-surface-sunken p-4">
        {tick}
        <Icon icon={AlertTriangle} size={18} className="text-warning-700" />
        <span className="min-w-0 flex-1 type-body text-text-body">A part in your basket is no longer sold. Untick it or remove it to check out.</span>
        {remove}
      </li>
    );
  }

  const over = line.problem === "not_enough_stock" && line.available_quantity !== null;
  return (
    <li className={cx("rounded-md border bg-surface-card p-4", line.problem ? "border-gold-300" : "border-border-subtle")}>
      <div className="flex items-start gap-3 md:items-center">
        {tick}
        <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-3 md:grid-cols-[minmax(0,1fr)_auto_7rem_2.5rem] md:items-center">
        <div className="min-w-0">
          <ReferenceNumber listing={listing} className="[font-size:var(--text-xs)] text-ink-600" />
          <span className="mt-0.5 block">
            <Link to={`/listings/${listing.id}`} className="type-body text-text-strong">
              {listing.name}
            </Link>
          </span>
          <span className="block type-small text-text-muted">
            {[listing.dimension, `${formatPesos(listing.price_cents)} ${unitLabel(listing.unit)}`].filter(Boolean).join(" · ")}
          </span>
        </div>
        <div className="col-start-2 row-start-1 md:hidden">{remove}</div>
        <QuantityStepper
          value={line.quantity}
          max={line.available_quantity !== null ? Math.max(line.quantity, line.available_quantity) : line.quantity}
          unit={quantityUnit(listing.unit, line.quantity)}
          onChange={onQuantity}
          disabled={busy || line.problem === "out_of_stock"}
          label={listing.name}
          size="sm"
        />
        <span className="text-right type-price [font-size:var(--text-base)] text-text-strong">{formatPesos(line.line_total_cents ?? 0)}</span>
        <div className="hidden md:block">{remove}</div>
        </div>
      </div>

      {line.problem === "out_of_stock" && (
        <Note tone="warning">Out of stock now. Remove it to check out; we can't take an order for it.</Note>
      )}
      {over && (
        <Note tone="warning">
          We have only {line.available_quantity} {quantityUnit(listing.unit, line.available_quantity!)}.{" "}
          <button type="button" disabled={busy} onClick={() => onQuantity(line.available_quantity!)} className="cursor-pointer type-label text-text-link underline">
            Change to {line.available_quantity}
          </button>
        </Note>
      )}
      {line.price_changed_from_cents !== null && (
        <Note tone="info">
          The price has changed since you added it: it was {formatPesos(line.price_changed_from_cents)} {unitLabel(listing.unit)}.
        </Note>
      )}
      {error && (
        <p role="alert" className="mt-3 mb-0 type-small text-danger-700">
          {error}
        </p>
      )}
    </li>
  );
}

function Note({ tone, children }: { tone: "warning" | "info"; children: ReactNode }) {
  return (
    <p className={cx("mt-3 mb-0 flex items-start gap-2 type-small", tone === "warning" ? "text-warning-700" : "text-ink-600")}>
      <Icon icon={tone === "warning" ? AlertTriangle : Info} size={16} className={cx("mt-0.5 flex-none", tone === "info" && "text-gold-700")} />
      <span>{children}</span>
    </p>
  );
}
